import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, ArrowRight, ArrowLeft, CheckCircle, Loader2, Globe, Search, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API, getContentSource } from "@/lib/constants";
import { hubspotContext } from "@/lib/hubspot";

const STEPS = [
  { id: "site", label: "Your Site" },
  { id: "contact", label: "Contact Info" },
];

const FreeAuditModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    website_url: "", target_keywords: "", competitors: "",
    current_traffic: "", biggest_challenge: "",
    first_name: "", last_name: "", email: "", phone: "",
    business_name: "", industry: ""
  });

  if (!isOpen) return null;

  const update = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const canAdvance = () => {
    if (step === 0) return form.website_url && form.biggest_challenge;
    if (step === 1) return form.first_name && form.last_name && form.email && form.phone;
    return true;
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/leads/service-inquiry`, {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        website_url: form.website_url,
        business_type: form.business_name || "Not specified",
        industry: form.industry || "Not specified",
        business_size: "Not specified",
        worked_with_agency: "Not specified",
        seo_understanding: "Not specified",
        business_scope: "Not specified",
        crm_tools: "",
        budget: "Free Audit Request",
        timeline: "ASAP",
        additional_notes: `Keywords: ${form.target_keywords || "N/A"}\nCompetitors: ${form.competitors || "N/A"}\nCurrent Traffic: ${form.current_traffic || "N/A"}\nBiggest Challenge: ${form.biggest_challenge}`,
        service: "Free AI Visibility Audit",
        ...hubspotContext()
      });
      onClose();
      navigate("/thank-you/lead?from=audit");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(0);
    setSubmitted(false);
    setForm({
      website_url: "", target_keywords: "", competitors: "",
      current_traffic: "", biggest_challenge: "",
      first_name: "", last_name: "", email: "", phone: "",
      business_name: "", industry: ""
    });
    onClose();
  };

  const RadioOption = ({ field, value, label }) => (
    <button
      type="button"
      onClick={() => update(field, value)}
      className={`w-full text-left px-4 py-3 rounded-lg border transition-all text-sm ${
        form[field] === value
          ? "border-[#2ABFBF] bg-[#2ABFBF]/10 text-white"
          : "border-white/10 bg-white/5 text-gray-400 hover:border-white/20 hover:bg-white/10"
      }`}
    >
      {label}
    </button>
  );

  if (submitted) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-lead-modal onClick={handleClose}>
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg p-8 text-center" onClick={e => e.stopPropagation()}>
          <div className="w-16 h-16 bg-[#2ABFBF]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-[#2ABFBF]" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-3" data-testid="audit-success-heading">Your AI Visibility Audit Is On the Way</h2>
          <p className="text-gray-400 mb-6">
            Thanks, {form.first_name}! We'll analyze <span className="text-[#2ABFBF]">{form.website_url}</span> for traditional SEO, AI search citations, and voice search visibility — expect your custom report within 1 business day.
          </p>
          <Button onClick={handleClose} className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold px-8" data-testid="audit-done-btn">
            Done
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" data-lead-modal onClick={handleClose}>
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative bg-[#111] border border-white/10 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
        data-testid="free-audit-modal"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#111] border-b border-white/10 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-white">Free AI Visibility Audit</h2>
            <p className="text-gray-500 text-xs mt-0.5">Step {step + 1} of {STEPS.length} — {STEPS[step].label}</p>
          </div>
          <button onClick={handleClose} className="text-gray-500 hover:text-white p-1" data-testid="audit-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-[#2ABFBF]" : "bg-white/10"}`} />
            ))}
          </div>
        </div>

        {/* Form */}
        <form
          id="free-audit-form"
          name="free-audit-form"
          className="px-6 py-5 space-y-4"
          onSubmit={(e) => { e.preventDefault(); if (step === STEPS.length - 1 && canAdvance() && !submitting) handleSubmit(); }}
        >
          {step === 0 && (
            <>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Website URL *</label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input name="website_url" value={form.website_url} onChange={e => update("website_url", e.target.value)} placeholder="https://yourwebsite.com" className="bg-white/5 border-white/10 text-white pl-10" data-testid="audit-website" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Target Keywords</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input name="target_keywords" value={form.target_keywords} onChange={e => update("target_keywords", e.target.value)} placeholder="e.g. plumber dallas, emergency plumbing" className="bg-white/5 border-white/10 text-white pl-10" data-testid="audit-keywords" />
                </div>
                <p className="text-gray-600 text-xs mt-1">Comma-separated keywords you want to rank for</p>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Top Competitors</label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  <Input name="competitors" value={form.competitors} onChange={e => update("competitors", e.target.value)} placeholder="e.g. competitor1.com, competitor2.com" className="bg-white/5 border-white/10 text-white pl-10" data-testid="audit-competitors" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">Current monthly organic traffic?</label>
                <div className="grid grid-cols-2 gap-2">
                  {["Not sure", "Under 1,000 visits", "1,000 - 10,000 visits", "10,000 - 50,000 visits", "50,000+ visits"].map(v => (
                    <RadioOption key={v} field="current_traffic" value={v} label={v} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-2 block">Biggest visibility challenge right now? *</label>
                <div className="space-y-2">
                  {[
                    "Not ranking for target keywords",
                    "Losing traffic or rankings",
                    "Not showing up in AI answers (ChatGPT, Perplexity)",
                    "Competitors outranking us",
                    "New website — need to build visibility",
                    "Don't know where to start"
                  ].map(v => (
                    <RadioOption key={v} field="biggest_challenge" value={v} label={v} />
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <input type="hidden" name="website_url" value={form.website_url} />
              <input type="hidden" name="target_keywords" value={form.target_keywords} />
              <input type="hidden" name="competitors" value={form.competitors} />
              <input type="hidden" name="current_traffic" value={form.current_traffic} />
              <input type="hidden" name="biggest_challenge" value={form.biggest_challenge} />
              <input type="hidden" name="content_source" value={getContentSource()} />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-gray-400 text-xs font-medium mb-1.5 block">First Name *</label>
                  <Input name="first_name" value={form.first_name} onChange={e => update("first_name", e.target.value)} placeholder="John" className="bg-white/5 border-white/10 text-white" data-testid="audit-first-name" />
                </div>
                <div>
                  <label className="text-gray-400 text-xs font-medium mb-1.5 block">Last Name *</label>
                  <Input name="last_name" value={form.last_name} onChange={e => update("last_name", e.target.value)} placeholder="Smith" className="bg-white/5 border-white/10 text-white" data-testid="audit-last-name" />
                </div>
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Business Name</label>
                <Input name="business_name" value={form.business_name} onChange={e => update("business_name", e.target.value)} placeholder="Your Company LLC" className="bg-white/5 border-white/10 text-white" data-testid="audit-business" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Email *</label>
                <Input type="email" name="email" value={form.email} onChange={e => update("email", e.target.value)} placeholder="john@company.com" className="bg-white/5 border-white/10 text-white" data-testid="audit-email" />
              </div>
              <div>
                <label className="text-gray-400 text-xs font-medium mb-1.5 block">Phone *</label>
                <Input type="tel" name="phone" value={form.phone} onChange={e => update("phone", e.target.value)} placeholder="(214) 555-1234" className="bg-white/5 border-white/10 text-white" data-testid="audit-phone" />
              </div>
              <div className="bg-[#2ABFBF]/5 border border-[#2ABFBF]/20 rounded-xl p-4">
                <p className="text-sm text-gray-300"><span className="text-[#2ABFBF] font-semibold">What happens next?</span> We'll run your site through our AI Visibility Scorecard — checking traditional SEO rankings, AI citation frequency, schema health, and voice search readiness. Expect your custom report within 1 business day.</p>
              </div>
            </>
          )}
        </form>

        {/* Footer */}
        <div className="sticky bottom-0 bg-[#111] border-t border-white/10 px-6 py-4 flex items-center justify-between">
          {step > 0 ? (
            <Button type="button" variant="ghost" onClick={() => setStep(step - 1)} className="text-gray-400 hover:text-white" data-testid="audit-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          ) : <div />}

          {step < STEPS.length - 1 ? (
            <Button
              type="button"
              onClick={() => setStep(step + 1)}
              disabled={!canAdvance()}
              className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold disabled:opacity-40"
              data-testid="audit-next-btn"
            >
              Next <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              type="submit"
              form="free-audit-form"
              disabled={!canAdvance() || submitting}
              className="bg-[#2ABFBF] hover:bg-[#1E9999] text-black font-semibold disabled:opacity-40"
              data-testid="audit-submit-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Get My Free Audit"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FreeAuditModal;
